import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Flex,
  Heading,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Get } from '../api/api3Client';

const AUTHORITY_LABELS = {
  fleet: 'Fleet',
  insurance: 'Insurance',
  both: 'Both',
};

const LIST_TABS = [
  { id: 'programs', label: 'Programs' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'drivers', label: 'Drivers' },
];

const ENROLLMENT_LABELS = {
  enrolled: 'Enrolled',
  pending_add: 'Pending (carrier processing)',
};

function authorityLabel(value) {
  return AUTHORITY_LABELS[value] || value || '—';
}

function td(extra = {}) {
  return { padding: '0.4rem 0.5rem', ...extra };
}

function formatDay(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    const ms = value > 1e12 ? value : value * 1000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function programLabel(p) {
  return p.name || p.externalKey || `Program ${p.programMasterId}`;
}

function programSearchText(p) {
  return [
    p.name,
    p.externalKey,
    p.policyNumber,
    p.carrierCompany,
    p.fleetName,
    p.fleetCode,
    p.fleetId,
  ]
    .filter((v) => v != null && v !== '')
    .join(' ')
    .toUpperCase();
}

function vehicleSearchText(v, p) {
  return [
    v.vin,
    v.name,
    v.plate,
    v.plateState,
    v.make,
    v.model,
    v.year,
    v.enrollmentStatus,
    programSearchText(p),
  ]
    .filter((x) => x != null && x !== '')
    .join(' ')
    .toUpperCase();
}

function driverSearchText(d, p) {
  return [
    d.nameFirst,
    d.nameLast,
    d.email,
    d.dlNumber,
    d.dlState,
    d.driverId,
    d.endorsementBucket,
    d.status,
    programSearchText(p),
  ]
    .filter((x) => x != null && x !== '')
    .join(' ')
    .toUpperCase();
}

function policyHref(p, tab) {
  return `/insurance-programs/${p.programMasterId}?tab=${tab}`;
}

function PolicyGroupHeader({ program, tab }) {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="baseline"
      wrap="wrap"
      marginTop="1.25rem"
      marginBottom="0.35rem"
      gap="0.5rem"
    >
      <View>
        <Heading level={5} style={{ margin: 0 }}>
          {programLabel(program)}
        </Heading>
        <Text fontSize="0.85rem" color="gray">
          {program.policyNumber || '—'} · {program.carrierCompany || '—'} ·{' '}
          {program.fleetName || program.fleetCode || program.fleetId}
        </Text>
      </View>
      <Link to={policyHref(program, tab)}>Open policy — live carrier + actions</Link>
    </Flex>
  );
}

export default function Insuranceprograms() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedFleetCode, setSelectedFleetCode] = useState(null);
  const [tab, setTab] = useState('programs');
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fleetsParam = params.get('fleets');
    setSelectedFleetCode(fleetsParam ? fleetsParam.split(',')[0] || null : null);
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (LIST_TABS.some((t) => t.id === tabParam)) setTab(tabParam);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError('');
      try {
        const query = {};
        if (selectedFleetCode) query.fleetCode = selectedFleetCode;
        const data = await api3Get('/insurance/endorsement-programs', query);
        if (!cancelled) setPrograms(data?.programs || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load programs');
      }
      if (!cancelled) setIsLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedFleetCode]);

  function updateTab(next) {
    setTab(next);
    const params = new URLSearchParams(location.search);
    if (next === 'programs') params.delete('tab');
    else params.set('tab', next);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }

  const needle = filter.trim().toUpperCase();

  const vehicleGroups = useMemo(() => {
    return programs
      .map((p) => {
        const rows = p.vehicles || [];
        const keepAll = !needle || programSearchText(p).includes(needle);
        const matched = keepAll
          ? rows
          : rows.filter((v) => vehicleSearchText(v, p).includes(needle));
        return { program: p, rows: matched };
      })
      .filter((g) => g.rows.length > 0);
  }, [programs, needle]);

  const driverGroups = useMemo(() => {
    return programs
      .map((p) => {
        const rows = p.drivers || [];
        const keepAll = !needle || programSearchText(p).includes(needle);
        const matched = keepAll
          ? rows
          : rows.filter((d) => driverSearchText(d, p).includes(needle));
        return { program: p, rows: matched };
      })
      .filter((g) => g.rows.length > 0);
  }, [programs, needle]);

  const vehicleCount = programs.reduce((n, p) => n + (p.vehicles || []).length, 0);
  const driverCount = programs.reduce((n, p) => n + (p.drivers || []).length, 0);

  return (
    <View padding="1rem">
      <Heading level={3}>Insurance Policy Management</Heading>
      <Text>
        Ago endorsements across policies. This list does not call the carrier.
        Open a policy to compare live with the carrier and to enroll, unenroll,
        or remove.
      </Text>
      {selectedFleetCode ? (
        <Text marginTop="0.5rem">Fleet filter: {selectedFleetCode}</Text>
      ) : (
        <Text marginTop="0.5rem">Showing all fleets in your access list.</Text>
      )}
      {error ? (
        <Alert variation="error" marginTop="1rem" isDismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Flex gap="0.5rem" marginTop="1rem" marginBottom="0.5rem" wrap="wrap">
        {LIST_TABS.map((t) => {
          const badge =
            t.id === 'vehicles' ? vehicleCount : t.id === 'drivers' ? driverCount : programs.length;
          return (
            <Button
              key={t.id}
              size="small"
              variation={tab === t.id ? 'primary' : undefined}
              onClick={() => updateTab(t.id)}
            >
              {t.label}
              {badge != null ? ` (${badge})` : ''}
            </Button>
          );
        })}
      </Flex>

      {tab !== 'programs' ? (
        <TextField
          label={tab === 'vehicles' ? 'Filter vehicles' : 'Filter drivers'}
          labelHidden
          placeholder={
            tab === 'vehicles'
              ? 'Filter by VIN, name, plate, policy'
              : 'Filter by name, email, DL, policy'
          }
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          width="24rem"
        />
      ) : null}

      {isLoading ? (
        <Loadingmini />
      ) : programs.length === 0 ? (
        <Text marginTop="1rem">No endorsement-managed programs for the fleets in scope.</Text>
      ) : tab === 'programs' ? (
        <View marginTop="0.5rem">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                <th style={td()}>Program</th>
                <th style={td()}>Policy #</th>
                <th style={td()}>Fleet</th>
                <th style={td()}>Carrier</th>
                <th style={td()}>VIN authority</th>
                <th style={td()}>Driver authority</th>
                <th style={td()}>Vehicles</th>
                <th style={td()}>Drivers</th>
                <th style={td()}>Overnight out of sync</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((p) => (
                <tr key={p.programMasterId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td()}>
                    <Link to={policyHref(p, 'vehicles')}>{programLabel(p)}</Link>
                  </td>
                  <td style={td()}>{p.policyNumber || '—'}</td>
                  <td style={td()}>{p.fleetName || p.fleetCode || p.fleetId}</td>
                  <td style={td()}>{p.carrierCompany || '—'}</td>
                  <td style={td()}>{authorityLabel(p.vehicleEndorsementAuthority)}</td>
                  <td style={td()}>{authorityLabel(p.driverEndorsementAuthority)}</td>
                  <td style={td()}>
                    <Link to={policyHref(p, 'vehicles')}>{p.approvedVehicleCount}</Link>
                  </td>
                  <td style={td()}>
                    <Link to={policyHref(p, 'drivers')}>{p.approvedDriverCount}</Link>
                  </td>
                  <td style={td()}>
                    {p.openDiscrepancyCount > 0 ? (
                      <Link to={policyHref(p, 'sync')}>
                        <Badge variation="error">{p.openDiscrepancyCount}</Badge>
                      </Link>
                    ) : (
                      <Badge variation="success">0</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Text fontSize="0.85rem" color="gray" marginTop="0.75rem">
            Overnight out of sync is from the last scheduled compare. Open a
            policy for a live carrier compare and to enroll, unenroll, or remove.
          </Text>
        </View>
      ) : tab === 'vehicles' ? (
        <View>
          {vehicleGroups.length === 0 ? (
            <Text marginTop="0.75rem">No matching endorsed vehicles.</Text>
          ) : (
            vehicleGroups.map(({ program, rows }) => (
              <View key={program.programMasterId}>
                <PolicyGroupHeader program={program} tab="vehicles" />
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                      <th style={td()}>Vehicle</th>
                      <th style={td()}>VIN</th>
                      <th style={td()}>Plate</th>
                      <th style={td()}>Ago status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.vehicleId} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={td()}>
                          {v.name || '—'}
                          <br />
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>
                            {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                          </span>
                        </td>
                        <td style={td()}>{v.vin || '—'}</td>
                        <td style={td()}>{v.plate || '—'}</td>
                        <td style={td()}>
                          {ENROLLMENT_LABELS[v.enrollmentStatus] || v.enrollmentStatus}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </View>
            ))
          )}
        </View>
      ) : (
        <View>
          {driverGroups.length === 0 ? (
            <Text marginTop="0.75rem">No matching endorsed drivers.</Text>
          ) : (
            driverGroups.map(({ program, rows }) => (
              <View key={program.programMasterId}>
                <PolicyGroupHeader program={program} tab="drivers" />
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                      <th style={td()}>Driver</th>
                      <th style={td()}>DL</th>
                      <th style={td()}>Enrolled</th>
                      <th style={td()}>Ago status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.driverId} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={td()}>
                          {`${d.nameFirst || ''} ${d.nameLast || ''}`.trim() || d.driverId}
                          <br />
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>{d.email || ''}</span>
                        </td>
                        <td style={td()}>
                          {d.dlState} {d.dlNumber}
                        </td>
                        <td style={td()}>{formatDay(d.enrolledAt) || '—'}</td>
                        <td style={td()}>
                          {d.endorsementBucket === 'approved' ? (
                            <Badge variation="success">approved</Badge>
                          ) : (
                            <Badge variation="info">{d.status || d.endorsementBucket}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </View>
            ))
          )}
        </View>
      )}
    </View>
  );
}
